import * as Log from '../../../util/log';
import { isNil } from '../../../util/object';
import { DEV } from '../../../constants';

/**
 * Compares two lists of arguments for shallow equality
 * Borrowed from reselect
 *
 * @param {Array} prev
 * @param {Array} next
 * @returns {boolean}
 */
const areArgumentsShallowlyEqual = (prev, next) => {
  if (prev === null && next === null) {
    return true;
  }

  if (prev === null || next === null || prev.length !== next.length) {
    return false;
  }

  // Do this in a for loop (and not a `forEach` or an `every`) so we can determine equality as fast as possible.
  const length = prev.length;
  for (let i = 0; i < length; i++) {
    if (prev[i] !== next[i]) {
      return false;
    }
  }

  return true;
};

/**
 * Works like reselect's "createSelector" method but creates individual cache entries based
 * on the second parameter. For example - someSelector(state, elementId) will be cached by
 * elementId.
 *
 * @param selectorFunctions - list of selectorFns to use as arguments for memoizaiton
 * @param combiner - method that handles results from selectorFns list
 * @returns {Function}
 */
export const createKeyedStateSelector = (selectorFunctions, combiner) => {
  const cache = {};

  return (state, cacheKey, ...restArgs) => {
    let cacheObj = cache[cacheKey];
    if (cacheObj && cacheObj.state === state) {
      return cacheObj.result;
    }

    const methodArgs = selectorFunctions.map((selectorFunction) => {
      return selectorFunction.call(null, state, cacheKey, ...restArgs);
    });

    if (cacheObj && areArgumentsShallowlyEqual(cacheObj.args, methodArgs)) {
      cacheObj.state = state;
      return cacheObj.result;
    }

    const result = combiner.apply(null, methodArgs);

    if (isNil(cacheKey)) return result;

    if (!cacheObj) {
      cache[cacheKey] = {};
      cacheObj = cache[cacheKey];
    }
    cacheObj.args = methodArgs;
    cacheObj.result = result;
    cacheObj.state = state;

    if (DEV && restArgs.length > 0 && !cacheObj.warning) {
      Log.warn(
        'createKeyedStateSelector may not behave as expected with more arguments',
      );
      cacheObj.warning = 'Possibly too many arguments';
    }

    return result;
  };
};
